import { HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';

/**
 * Request context key used by signal-store resources to pass the active customer id.
 *
 * Default is `null`, which means "do not force/override a customer header".
 */
export const SIGNAL_STORE_CUSTOMER_ID = new HttpContextToken<number | null>(() => null);

/**
 * Header consumed by backend endpoints to resolve customer context.
 */
export const SIGNAL_STORE_CUSTOMER_HEADER = 'Next-CustomerId';
export const SIGNAL_STORE_LOCATION_HEADER = 'Next-LocationId';

/**
 * Creates request options that attach signal-store customer context.
 *
 * Use this in services called from signal-store resources.
 *
 * Why:
 * - The global customer interceptor is not aware of feature-signal-store resource params.
 * - Resource reloads depend on `params` (including customer id) changing.
 * - Passing `customerId` through request context lets `SignalStoreCustomerInterceptor`
 *   stamp the correct header for each resource request.
 *
 * @example
 * ```ts
 * return this.http.get<Response>(url, {
 *   params,
 *   ...signalStoreCustomerRequestOptions(customerId),
 * });
 * ```
 */
export const signalStoreCustomerRequestOptions = (
  customerId?: number,
): { context?: HttpContext } =>
  customerId === undefined
    ? {}
    : {
      context: new HttpContext().set(SIGNAL_STORE_CUSTOMER_ID, customerId),
    };

export const signalStoreCustomerAndLocationRequestOptions = (
  customerId?: number,
  locationId?: number,
): { context?: HttpContext; headers?: HttpHeaders } => {
  const options = signalStoreCustomerRequestOptions(customerId);

  return locationId === undefined
    ? options
    : {
      ...options,
      headers: new HttpHeaders().set(SIGNAL_STORE_LOCATION_HEADER, locationId.toString()),
    };
};
